import type {
    OrthopulseShipToDefaults,
    ShipToPayload,
    ShipToTarget
} from '../../../../../services/orthopulseOrder';
import { COUNTRY_UNITED_STATES, normalizeCountry, normalizeRegion } from './shipToRegions';

export interface ShipToFormValues {
    name: string;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    email: string;
    phone: string;
}

export const EMPTY_SHIP_TO_FORM: ShipToFormValues = {
    name: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    country: COUNTRY_UNITED_STATES,
    email: '',
    phone: ''
};

// Form values plus, when a stored state/province couldn't be matched, the raw
// dropped value - callers surface it as a notice instead of losing it silently.
export interface SeededShipToForm {
    values: ShipToFormValues;
    unmatchedState: string | null;
}

export function practiceDefaultsToForm(
    defaults: OrthopulseShipToDefaults['practice']
): SeededShipToForm {
    const country = normalizeCountry(defaults.country);
    const rawState = (defaults.state ?? '').trim();
    const matchedState = normalizeRegion(rawState, country);

    return {
        values: {
            name: defaults.name ?? '',
            address: defaults.address ?? '',
            address2: defaults.address2 ?? '',
            city: defaults.city ?? '',
            state: matchedState,
            zip: defaults.zip ?? '',
            country,
            email: defaults.email ?? '',
            phone: defaults.phone ?? ''
        },
        unmatchedState: !matchedState && rawState ? rawState : null
    };
}

// Patients have no stored address - address fields start empty/required;
// country still normalizes (from no input) so the select has a valid value.
export function patientDefaultsToForm(
    defaults: OrthopulseShipToDefaults['patient']
): SeededShipToForm {
    return {
        values: {
            ...EMPTY_SHIP_TO_FORM,
            name: defaults.name,
            country: normalizeCountry(undefined),
            email: defaults.email ?? '',
            phone: defaults.phone ?? ''
        },
        unmatchedState: null
    };
}

// state is required by validateShipTo before Preview/Send are ever
// reachable, so trimming here is guaranteed non-empty - no `|| null` needed.
export function buildShipToPayload(target: ShipToTarget, values: ShipToFormValues): ShipToPayload {
    return {
        target,
        name: values.name.trim(),
        address: values.address.trim(),
        address2: values.address2.trim() || null,
        city: values.city.trim(),
        state: values.state.trim(),
        zip: values.zip.trim(),
        country: values.country.trim(),
        email: values.email.trim() || null,
        phone: values.phone.trim() || null
    };
}
